import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Event, EventAddon, EventPricesWithAddons } from 'src/models/events';
import { Observable, retry, shareReplay } from 'rxjs';
import { Service } from 'src/models/service';
import { EventType } from 'src/models/eventType';
import { EventAttendee, EventCartItems, EventDetails } from 'src/models/eventAttendee';
import { User } from 'src/models/user';
import { KeyValueTable } from 'src/models/myKeyValue';
import { EventQuestion, FieldTypeLookup, Question, QuestionAnswer, QuestionAnswerReport } from 'src/models/question';
import { AttendeePicture } from 'src/models/attendeePicture';
import { AppSettings } from '../app-settings';
import { PaymentMethod } from 'src/models/paymentMethod';
import { RegisterEventAttendeeRequest } from 'src/models/registerEventAttendeeRequest';
import { EventPaymentDetail } from 'src/models/eventPaymentDetail';
import { EventCashPayment } from 'src/models/eventCashPayment';

@Injectable({
    providedIn: 'root'
})
export class EventService {
    httpOptions = {
        headers: new HttpHeaders({
            'Content-Type': 'application/json'
        })
    };

    constructor(private http: HttpClient, private appSettings: AppSettings) { }

    getPaymentMethods(): Observable<PaymentMethod[]> {
        let url = this.appSettings.apiBaseUrl() + 'Events/GetPaymentMethods?skipLoadingSpinner=1';

        return this.http.get<PaymentMethod[]>(url, this.httpOptions)
            .pipe(
                retry({ count: 1, delay: 1000 }), shareReplay());
    }

    getMyEvents(profileId: number | undefined, isFutureEvents: boolean, eventTypeId: number, eventLocation: string, speakerId: number): Observable<Event[]> {
        let url = this.appSettings.apiBaseUrl() + 'Events/GetMyEvents?profileId=' + profileId + "&isFutureEvents=" + isFutureEvents +
            "&eventTypeId=" + eventTypeId + "&eventLocation=" + eventLocation + "&speakerId=" + speakerId;

        return this.http.get<Event[]>(url, this.httpOptions)
            .pipe(
                retry({ count: 1, delay: 1000 }), shareReplay());
    }

    getEventById(id: number, isEdit: boolean = false): Observable<Event> {
        let url = this.appSettings.apiBaseUrl() + `Events/GetEventById?eventId=${id}&isEdit=${isEdit}`;

        return this.http.get<Event>(url, this.httpOptions)
            .pipe(
                retry({ count: 1, delay: 1000 }), shareReplay());
    }

    getEventPricesWithAddons(eventId): Observable<EventPricesWithAddons[]> {
        let url = this.appSettings.apiBaseUrl() + `Events/GetEventPricesWithAddons?skipLoadingSpinner=1&eventId=${eventId}`;

        return this.http.get<EventPricesWithAddons[]>(url, this.httpOptions)
            .pipe(
                retry({ count: 1, delay: 1000 }), shareReplay());

    }

    getEventFlyer(eventId: number): Observable<string> {
        let url = this.appSettings.apiBaseUrl() + `Events/GetEventFlyer?skipLoadingSpinner=1&eventId=${eventId}`;

        return this.http.get<string>(url, this.httpOptions);
    }

    getEventAdmins(id: number): Observable<EventAttendee[]> {
        let url = this.appSettings.apiBaseUrl() + `Events/GetEventAdmins?skipLoadingSpinner=1&eventId=${id}`;

        return this.http.get<EventAttendee[]>(url, this.httpOptions)
            .pipe(
                retry({ count: 1, delay: 1000 }), shareReplay());
    }


    getEventPossibleAttendees(id: number): Observable<EventAttendee[]> {
        let url = this.appSettings.apiBaseUrl() + 'Events/GetEventPossibleAttendees?skipLoadingSpinner=1&eventId=' + id;

        return this.http.get<EventAttendee[]>(url, this.httpOptions)
            .pipe(
                retry({ count: 1, delay: 1000 }), shareReplay());
    }

    getEligibleEventMembers(eventId: number, profileId: number): Observable<EventAttendee[]> {
        let url = this.appSettings.apiBaseUrl() + 'Events/GetEligibleEventMembers?skipLoadingSpinner=1&eventId=' + eventId + '&profileId=' + profileId

        return this.http.get<EventAttendee[]>(url, this.httpOptions)
            .pipe(
                retry({ count: 1, delay: 1000 }), shareReplay());
    }

    getEventAttendees(id: number, userId: number): Observable<EventDetails> {
        let url = this.appSettings.apiBaseUrl() + 'Events/GetEventAttendees?skipLoadingSpinner=1&eventId=' + id + '&userId=' + userId;

        return this.http.get<EventDetails>(url, this.httpOptions)
            .pipe(
                retry({ count: 1, delay: 1000 }), shareReplay());
    }

    getMyEventServices(userId: number): Observable<Service[]> {
        let url = this.appSettings.apiBaseUrl() + 'Events/GetEventServicesByUserId?userId=' + userId;

        return this.http.get<Service[]>(url, this.httpOptions)
            .pipe(
                retry({ count: 1, delay: 1000 }), shareReplay());
    }

    getSundaySchoolServices(): Observable<Service[]> {
        let url = this.appSettings.apiBaseUrl() + 'Events/GetSundaySchoolServices';

        return this.http.get<Service[]>(url, this.httpOptions)
            .pipe(
                retry({ count: 1, delay: 1000 }), shareReplay());
    }

    getEventTypes(): Observable<EventType[]> {
        let url = this.appSettings.apiBaseUrl() + 'Events/GetEventTypes';

        return this.http.get<EventType[]>(url, this.httpOptions)
            .pipe(
                retry({ count: 1, delay: 1000 }), shareReplay());
    }

    getEventFilters(): Observable<KeyValueTable[]> {
        let url = this.appSettings.apiBaseUrl() + 'Events/GetEventFilters';

        return this.http.get<KeyValueTable[]>(url, this.httpOptions)
            .pipe(
                retry({ count: 1, delay: 1000 }), shareReplay());
    }

    getEventDataTypes(): Observable<KeyValueTable[]> {
        let url = this.appSettings.apiBaseUrl() + 'Events/GetEventDataTypes';

        return this.http.get<KeyValueTable[]>(url, this.httpOptions)
            .pipe(
                retry({ count: 1, delay: 1000 }), shareReplay());
    }

    getAllUsers(): Observable<User[]> {
        let url = this.appSettings.apiBaseUrl() + 'Events/GetAllUsersNames';

        return this.http.get<User[]>(url, this.httpOptions)
            .pipe(
                retry({ count: 1, delay: 1000 }), shareReplay());
    }

    getEventLocations(): Observable<string[]> {
        let url = this.appSettings.apiBaseUrl() + 'Events/GetEventLocations';

        return this.http.get<string[]>(url, this.httpOptions)
            .pipe(
                retry({ count: 1, delay: 1000 }), shareReplay());
    }

    UpdateCashPayment(request: EventCashPayment): Observable<EventPaymentDetail> {
        let url = this.appSettings.apiBaseUrl() + 'Events/UpdateCashPayment';
        return this.http.post<EventPaymentDetail>(url, request, this.httpOptions)
            .pipe(
                retry({ count: 1, delay: 1000 }), shareReplay());
    }

    RegisterEventAttendee(eventId: number, profileId: number, isPaid: boolean, createdBy: number, paymentMethodId, amountPaid): Observable<EventPaymentDetail> {
        let url = this.appSettings.apiBaseUrl() + 'Events/RegistrationEventAttendeeAddons';
        let request: RegisterEventAttendeeRequest = {
            EventId: eventId,
            ProfileId: profileId,
            PaymentMethodId: paymentMethodId,
            CreatedBy: createdBy,
            IsPaid: isPaid,
            ModifiedBy: createdBy,
            ModifiedDate: new Date(),
            PaidAmount: amountPaid
        };

        return this.http.post<EventPaymentDetail>(url, request, this.httpOptions)
            .pipe(
                retry({ count: 1, delay: 1000 }), shareReplay());
    }

    RegisterEventAttendees(eventId: number, profileIds: string, isPaid: boolean, createdBy: number): Observable<boolean> {
        let url = this.appSettings.apiBaseUrl() + 'Events/RegisterEventAttendees?eventId=' +
            eventId + '&profileIds=' + profileIds + '&isPaid=' + isPaid + '&createdBy=' + createdBy;

        return this.http.get<boolean>(url, this.httpOptions)
            .pipe(
                retry({ count: 1, delay: 1000 }), shareReplay());
    }

    UnRegisterEventAttendee(eventId: number, profileId: number): Observable<EventPaymentDetail> {
        let url = this.appSettings.apiBaseUrl() + 'Events/UnRegisterEventAttendee?eventId=' +
            eventId + '&profileId=' + profileId;

        return this.http.get<EventPaymentDetail>(url, this.httpOptions)
            .pipe(
                retry({ count: 1, delay: 1000 }), shareReplay());
    }

    saveEvent(event: Event): Observable<number> {
        let url = this.appSettings.apiBaseUrl() + 'Events/SaveEvent';

        return this.http.post<number>(url, event, this.httpOptions)
            .pipe(
                retry({ count: 1, delay: 1000 }), shareReplay());
    }

    updateAttachmentName(attachmentId: number, newFileName: string): Observable<boolean> {
        let url = this.appSettings.apiBaseUrl() + 'Events/UpdateAttachmentName?attachmentId=' + attachmentId + '&newFileName=' + newFileName;

        return this.http.get<boolean>(url, this.httpOptions)
            .pipe(
                retry({ count: 1, delay: 1000 }), shareReplay());
    }

    deleteAttachmentById(attachmentId: number): Observable<boolean> {
        let url = this.appSettings.apiBaseUrl() + 'Events/DeleteAttachmentById?attachmentId=' + attachmentId;

        return this.http.get<boolean>(url, this.httpOptions)
            .pipe(
                retry({ count: 1, delay: 1000 }), shareReplay());
    }

    getQuestionsByCategory(categoryId: number): Observable<Question[]> {
        let url = this.appSettings.apiBaseUrl() + 'Events/GetQuestionsList?categoryId=' + categoryId;

        return this.http.get<Question[]>(url, this.httpOptions)
            .pipe(
                retry({ count: 1, delay: 1000 }), shareReplay());
    }

    saveEventQuestion(eventid?: number, questionId?: number): Observable<number> {
        let url = this.appSettings.apiBaseUrl() + 'Events/SaveEventQuestion?eventId=' + eventid + '&questionId=' + questionId;

        return this.http.get<number>(url, this.httpOptions)
            .pipe(
                retry({ count: 1, delay: 1000 }), shareReplay());
    }

    updateEventQuestionStatus(eventQuestionId: number, isDisabled: boolean): Observable<boolean> {
        let url = this.appSettings.apiBaseUrl() + 'Events/UpdateEventQuestionStatus?eventQuestionId=' + eventQuestionId + '&isDisabled=' + isDisabled;

        return this.http.get<boolean>(url, this.httpOptions)
            .pipe(
                retry({ count: 1, delay: 1000 }), shareReplay());
    }

    updateEventQuestionOrder(questions: EventQuestion[]): Observable<boolean> {
        let url = this.appSettings.apiBaseUrl() + 'Events/UpdateEventQuestionsOrder';

        return this.http.post<boolean>(url, questions, this.httpOptions)
            .pipe(
                retry({ count: 1, delay: 1000 }), shareReplay());
    }

    deleteEventQuestion(eventQuestionId: number): Observable<boolean> {
        let url = this.appSettings.apiBaseUrl() + 'Events/DeleteEventQuestion?eventQuestionId=' + eventQuestionId;

        return this.http.get<boolean>(url, this.httpOptions)
            .pipe(
                retry({ count: 1, delay: 1000 }), shareReplay());
    }

    getEventQuestionAnswers(eventId: number, profileId: number): Observable<QuestionAnswer[]> {
        let url = this.appSettings.apiBaseUrl() + 'Events/GetEventQuestionAnswers?eventId=' + eventId + '&profileId=' + profileId;

        return this.http.get<QuestionAnswer[]>(url, this.httpOptions)
            .pipe(
                retry({ count: 1, delay: 1000 }), shareReplay());
    }

    getEventQuestionOptionsList(questionId: number): Observable<FieldTypeLookup[]> {
        let url = this.appSettings.apiBaseUrl() + 'Events/GetEventQuestionOptionsList?questionId=' + questionId;

        return this.http.get<FieldTypeLookup[]>(url, this.httpOptions)
            .pipe(
                retry({ count: 1, delay: 1000 }), shareReplay());
    }

    getPaymentOrderDetails(eventId: number, profileId: number): Observable<EventPaymentDetail> {
        let url = `${this.appSettings.apiBaseUrl()}Events/GetPaymentOrderDetails?eventId=${eventId}&profileId=${profileId}`;
        return this.http.get<EventPaymentDetail>(url, this.httpOptions)
            .pipe(
                retry({ count: 1, delay: 1000 }), shareReplay());
    }

    saveQuestionAnswer(answers: QuestionAnswer[], userId: number): Observable<boolean> {
        let url = this.appSettings.apiBaseUrl() + 'Events/SaveQuestionAnswer?userId=' + userId;

        return this.http.post<boolean>(url, answers, this.httpOptions)
            .pipe(
                retry({ count: 1, delay: 1000 }), shareReplay());
    }

    getQuestionAnswerReport(eventId: number): Observable<QuestionAnswerReport[]> {
        let url = this.appSettings.apiBaseUrl() + 'Events/GetQuestionAnswersReport?eventId=' + eventId;

        return this.http.get<QuestionAnswerReport[]>(url, this.httpOptions)
            .pipe(
                retry({ count: 1, delay: 1000 }), shareReplay());
    }

    getEventWaiver(profileId: number, userProfileId: number, eventId: number): Observable<string> {
        let url = this.appSettings.apiBaseUrl() + `Events/GetEventWaiver?profileId=${profileId}&userProfileId=${userProfileId}&eventId=${eventId}`

        return this.http.get<string>(url, this.httpOptions)
            .pipe(
                retry({ count: 1, delay: 1000 }), shareReplay());
    }

    saveEventWaiverSignature(eventSignature: AttendeePicture): Observable<boolean> {
        let url = this.appSettings.apiBaseUrl() + `Events/SaveEventWaiverSignature`;

        return this.http.post<boolean>(url, eventSignature, this.httpOptions)
            .pipe(
                retry({ count: 1, delay: 1000 }), shareReplay());
    }

    saveAttendance(eventId: number, profileId: any, isChecked: any): Observable<boolean> {
        let url = this.appSettings.apiBaseUrl() + `Events/SaveEventAttendance?eventId=${eventId}&profileId=${profileId}&isAttended=${isChecked}`;

        return this.http.get<boolean>(url, this.httpOptions)
            .pipe(
                retry({ count: 1, delay: 1000 }), shareReplay());
    }

    savePaymentCartAddons(profileId: number, eventId: number, eventItemPriceId: number): Observable<EventAttendee[]> {
        let url = this.appSettings.apiBaseUrl() + `Events/SavePaymentCartAddons?eventId=${eventId}&profileId=${profileId}&eventItemPriceId=${eventItemPriceId}`;

        return this.http.get<EventAttendee[]>(url, this.httpOptions)
            .pipe(
                retry({ count: 1, delay: 1000 }), shareReplay());
    }

    saveEventAddon(addon: EventAddon): Observable<number> {
        let url = this.appSettings.apiBaseUrl() + `Events/SaveEventAddon`;

        return this.http.post<number>(url, addon, this.httpOptions)
            .pipe(
                retry({ count: 1, delay: 1000 }), shareReplay());
    }

    deleteEventAddon(eventItemPriceId: number): Observable<boolean> {
        let url = this.appSettings.apiBaseUrl() + 'Events/DeleteEventAddon?eventItemPriceId=' + eventItemPriceId;

        return this.http.delete<boolean>(url, this.httpOptions)
            .pipe(
                retry({ count: 1, delay: 1000 }), shareReplay());
    }

    getCartItemsByProfileId(profileId: number, eventId: number): Observable<EventCartItems[]> {
        let url = this.appSettings.apiBaseUrl() + `Events/GetCartItemsByProfileId?profileId=${profileId}&eventId=${eventId}`;

        return this.http.get<EventCartItems[]>(url, this.httpOptions)
            .pipe(
                retry({ count: 1, delay: 1000 }), shareReplay());
    }

    cancelUnpaidRegistration(eventId: number, profileId: number): Observable<boolean> {
        let url = this.appSettings.apiBaseUrl() + `Events/CancelUnpaidRegistration?profileId=${profileId}&eventId=${eventId}`;

        return this.http.get<boolean>(url, this.httpOptions)
            .pipe(
                retry({ count: 1, delay: 1000 }), shareReplay());

    }

    updateCartItemsPaidInCash(profileId: number, eventId: number): Observable<boolean> {
        let url = this.appSettings.apiBaseUrl() + `Events/UpdateCartItemsPaidInCash?profileId=${profileId}&eventId=${eventId}`;

        return this.http.get<boolean>(url, this.httpOptions)
            .pipe(
                retry({ count: 1, delay: 1000 }), shareReplay());
    }

    deleteEvent(eventId: number): Observable<boolean> {
        let url = this.appSettings.apiBaseUrl() + `Events/DeleteEvent?eventId=${eventId}`;

        return this.http.delete<boolean>(url, this.httpOptions)
            .pipe(retry({ count: 1, delay: 1000 }), shareReplay());
    }

}